/**
 * What a player can afford, when the balance may not be known yet.
 *
 * Boot opens the Take screen from a share link before the account has loaded. A balance of 0 there is not a
 * balance, it is the absence of one, and reading it as one told a player with 1,365 $CHAIN "Not enough $CHAIN".
 * So the balance is `null` until the account is known, and while it is unknown nothing is refused or disabled for
 * affordability: the server decides (`insufficient-balance`), and the screens re-evaluate the moment the account
 * (or any later change to it) arrives. Blockari's rule.
 */

/** The player's $CHAIN, or null while the account is not known. */
export type Balance = number | null;

/** Whether a wager is within reach: always, while the balance is unknown; otherwise by the balance or a held ticket. */
export function canAfford(fee: number, balance: Balance, tickets = 0): boolean {
  return balance === null || fee <= balance || tickets > 0;
}

/**
 * Whether to SAY the player is short: only with a known balance, a wager above it and nothing (a ticket) to pay
 * with. An unknown balance is never a shortfall.
 */
export function isShort(fee: number, balance: Balance, ticketOn = false): boolean {
  return balance !== null && !ticketOn && fee > balance;
}

/**
 * The wager to settle on when a screen opens: the last one if it is allowed and affordable; else the dearest chip
 * they can afford, else the smallest allowed. With the balance unknown the last wager stands (nothing says it is
 * unaffordable).
 */
export function settleFee(
  fee: number,
  chips: readonly number[],
  min: number,
  balance: Balance,
  allowed: boolean,
  tickets = 0,
): number {
  if (allowed && canAfford(fee, balance, tickets)) return fee;
  return (balance === null ? [] : chips.filter((f) => f <= balance)).pop() ?? min;
}

/**
 * A ticket covers a challenge at exactly its own fee, so "Use ticket" is on for ONE fee: the one the player turned
 * it on at. Moving to any other fee (or leaving and reopening the screen, or the ticket running out) turns it off,
 * and coming back to the fee does not turn it back on: a held ticket is never spent unless it was asked for at the
 * fee that is about to be paid. `at` is the fee it was turned on at (null: off); the settled value is returned.
 */
export function ticketAskedAt(at: number | null, fee: number, held: number): number | null {
  return at !== null && at === fee && held > 0 ? at : null;
}
